import { useCallback, useEffect, useRef, useState } from "react";
import {
	getJti,
	readCounter,
	saveCounter,
	parseSseEvents,
	extractChunkText,
	errorMessageForStatus,
	proximaPalavra,
	jaAquecido,
	marcarAquecido,
	WARM_UP_MESSAGE,
} from "./repondeur-utils";

// a API entrega a resposta inteira de uma vez: revelar palavra a palavra devolve
// a sensação do texto sendo escrito e continua válido quando o streaming real chegar
const INTERVALO_REVELACAO = 15;

export default function useRepondeur() {
	const [messages, setMessages] = useState([]);
	const [streaming, setStreaming] = useState(false);
	const [contexto, setContexto] = useState(null);
	const montadoRef = useRef(true);
	const contextoPromiseRef = useRef(null);
	const abortRef = useRef(null);
	const warmingRef = useRef(false);
	const warmPromiseRef = useRef(null);
	const revelacaoRef = useRef(null);

	const updateBotMessage = (id, text) => {
		setMessages(current => current.map(msg => (msg.id === id ? { ...msg, text } : msg)));
	};

	const sendMessage = async text => {
		const trimmed = (text || "").trim();
		if (!trimmed || streaming) return;

		const jti = getJti();
		const counter = readCounter(jti);
		const userId = counter + 1;
		const botId = counter + 2;

		setMessages(current => [
			...current,
			{ id: userId, from: "user", text: trimmed, at: new Date() },
			{ id: botId, from: "bot", text: "", at: new Date() },
		]);
		setStreaming(true);

		// se o aquecimento ainda estiver em curso, aguarda sem travar a interface
		if (warmingRef.current && warmPromiseRef.current) {
			try {
				await warmPromiseRef.current;
			} catch (e) {}
		}

		const controller = new AbortController();
		abortRef.current = controller;

		let botText = "";
		let counterSaved = false;
		let pendente = "";
		let exibido = "";
		let fluxoTerminado = false;

		const revelacao = new Promise(resolve => {
			const timer = setInterval(() => {
				if (pendente) {
					const parte = proximaPalavra(pendente);
					pendente = pendente.slice(parte.length);
					exibido += parte;
					updateBotMessage(botId, exibido);
					return;
				}
				if (fluxoTerminado) {
					clearInterval(timer);
					revelacaoRef.current = null;
					resolve();
				}
			}, INTERVALO_REVELACAO);

			revelacaoRef.current = {
				descartar: () => {
					pendente = "";
				},
			};
		});

		try {
			const response = await fetch(process.env.REACT_APP_LABS_API + "repondeur/chat.php", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + sessionStorage.getItem("session_token"),
				},
				body: JSON.stringify({ message: trimmed }),
				signal: controller.signal,
			});

			if (!response.ok) {
				const failure = errorMessageForStatus(response.status);
				setMessages(current =>
					current
						.filter(msg => msg.id !== botId)
						.concat([{ id: "err-" + botId, from: "system", text: failure }])
				);
				return;
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder("utf-8");
			let buffer = "";

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const { events, rest } = parseSseEvents(buffer);
				buffer = rest;

				for (const event of events) {
					if (event.event === "error") {
						setMessages(current =>
							current.concat([{ id: "err-" + botId, from: "system", text: errorMessageForStatus(503) }])
						);
						return;
					}
					const chunk = extractChunkText(event.data);
					if (chunk) {
						botText += chunk;
						pendente += chunk;
						if (!counterSaved) {
							saveCounter(jti, botId);
							counterSaved = true;
						}
					}
				}
			}

			if (!botText) {
				setMessages(current =>
					current
						.filter(msg => msg.id !== botId)
						.concat([{ id: "err-" + botId, from: "system", text: errorMessageForStatus(503) }])
				);
			}
		} catch (e) {
			if (e.name !== "AbortError") {
				setMessages(current =>
					current
						.filter(msg => msg.id !== botId || botText)
						.concat([{ id: "err-" + botId, from: "system", text: errorMessageForStatus(503) }])
				);
			}
		} finally {
			fluxoTerminado = true;
			// a interface só é liberada quando o texto termina de aparecer
			await revelacao;
			setStreaming(false);
			abortRef.current = null;
		}
	};

	useEffect(() => {
		return () => {
			montadoRef.current = false;
		};
	}, []);

	const carregarContexto = useCallback(() => {
		if (contextoPromiseRef.current) return contextoPromiseRef.current;

		contextoPromiseRef.current = fetch(process.env.REACT_APP_LABS_API + "repondeur/contexto.php", {
			headers: { Authorization: "Bearer " + sessionStorage.getItem("session_token") },
		})
			.then(resposta => (resposta.ok ? resposta.json() : null))
			.then(dados => {
				if (!dados || dados.status !== 200) {
					contextoPromiseRef.current = null;
					return null;
				}
				if (montadoRef.current) setContexto(dados);
				return dados;
			})
			.catch(() => {
				contextoPromiseRef.current = null;
				return null;
			});

		return contextoPromiseRef.current;
	}, []);

	// consome a primeira mensagem da conversa, que a API sempre responde de forma genérica
	const warmUp = useCallback(() => {
		const jti = getJti();
		if (jaAquecido(jti) || warmingRef.current) return warmPromiseRef.current;

		warmingRef.current = true;
		const counter = readCounter(jti);

		warmPromiseRef.current = (async () => {
			try {
				const response = await fetch(process.env.REACT_APP_LABS_API + "repondeur/chat.php", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: "Bearer " + sessionStorage.getItem("session_token"),
					},
					body: JSON.stringify({ message: WARM_UP_MESSAGE }),
				});

				if (response.ok && response.body) {
					const reader = response.body.getReader();
					while (true) {
						const { done } = await reader.read();
						if (done) break;
					}
					saveCounter(jti, counter);
					marcarAquecido(jti);
				}
			} catch (e) {
				// aquecimento é best-effort: em caso de falha o chat segue normalmente
			} finally {
				warmingRef.current = false;
			}
		})();

		return warmPromiseRef.current;
	}, []);

	const sendFeedback = useCallback(async (messageId, score, comment) => {
		try {
			const response = await fetch(process.env.REACT_APP_LABS_API + "repondeur/feedback.php", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: "Bearer " + sessionStorage.getItem("session_token"),
				},
				body: JSON.stringify({ message_id: messageId, score, comment: comment || "" }),
			});
			if (!response.ok) return false;
			setMessages(current =>
				current.map(msg => (msg.id === messageId ? { ...msg, feedbackSent: true } : msg))
			);
			return true;
		} catch (e) {
			return false;
		}
	}, []);

	const clearVisual = useCallback(() => {
		if (abortRef.current) {
			abortRef.current.abort();
		}
		if (revelacaoRef.current) {
			revelacaoRef.current.descartar();
		}
		setMessages([]);
	}, []);

	return { messages, streaming, contexto, sendMessage, sendFeedback, clearVisual, warmUp, carregarContexto };
}
